import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import _ from "lodash";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import {
  selectCheckoutInfo,
  selectCheckoutShipping,
  // selectEstimatedTotal,
  selectPaymentIsBeingProcessed,
} from "../../redux/checkout/checkout.selectors";
import { selectCartItems } from "../../redux/cart/cart.selectors";
import {
  getPaymentMethods,
  updatePaymentMethod,
} from "../../apis/payment.apis";
import { confirmCheckout, emptyCart } from "../../apis/shop.services.apis";
import { checkoutActions } from "../../redux/checkout/checkout.reducer";

import consoleLog from "../../utils/consoleLog";

import withAuth from "../../hocs/withAuth";

import useStyles, { stripeElementStyle } from "./checkout-payment.styles";
import "./CardSectionStyles.css";

import StripeIcon from "../../assets/icons/stripe-icon";
import CheckoutLinks from "../../components/checkout-links";
import CheckoutSummary from "../../components/checkout-summary";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "../../components/checkout-accordion/checkout-accordion.component";
import OrderIsBeingProcessed from "../../components/order-is-being-processed";
import Loading from "../../components/loading";

const CheckoutPaymentPage = ({
  checkoutRoutes,
}: {
  checkoutRoutes: string[];
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const history = useHistory();
  const dispatch = useDispatch();

  const { contact, shippingAddress } = useSelector(selectCheckoutInfo);
  const cartItems = useSelector(selectCartItems);
  const checkoutShipping = useSelector(selectCheckoutShipping);
  // const estimatedTotal = useSelector(selectEstimatedTotal);
  const [currentCustomerId, setCurrentCustomerId] =
    useState("cus_JBG5WPOsANVaCS");
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<any[] | null>(
    null,
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  const [isAddingNewCard, setIsAddingNewCard] = useState(false);
  const [inputAddress, setInputAddress] = useState<{
    firstname: string;
    lastname: string;
    company?: string;
    line1: string;
    line2: string;
    country: string;
    state: string;
    city: string;
    postal_code: string;
    phone: string;
  }>({
    firstname: "",
    lastname: "",
    company: "",
    line1: "",
    line2: "",
    country: "",
    state: "",
    city: "",
    postal_code: "",
    phone: "",
  });
  const [billingAddress, setBillingAddress] = useState<any>({
    address: {
      city: "",
      country: "",
      line1: "",
      line2: "",
      postal_code: "",
      state: "",
    },
    email: "",
    name: "",
    phone: "",
  });
  const [sameAsShippingAddress, setSameAsShippingAddress] =
    useState<boolean>(true);
  const isBeingProcessed = useSelector(selectPaymentIsBeingProcessed);

  const matches = useMediaQuery("(min-width:1200px)");
  const [expanded, setExpanded] = useState<string | false>("panel1");
  const [AddressExpanded, setAddressExpanded] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 500);

    window.scrollTo({
      top: 0,
    });
  }, []);

  const classes = useStyles({ matches });

  const handleAccordionChange =
    (panel: string) => (event: ChangeEvent<{}>, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  // Get saved payment methods
  useEffect(() => {
    getPaymentMethods({ customerId: currentCustomerId })
      .then((res) => setSavedPaymentMethods(res.body?.data))
      .catch((err) => err.message);
  }, [location, currentCustomerId]);

  // Set default payment method => Need to be fixed
  useEffect(() => {
    setSelectedPaymentMethod(savedPaymentMethods?.[0]);
    if (savedPaymentMethods?.length === 0) {
      setIsAddingNewCard(true);
    }
  }, [savedPaymentMethods]);

  useEffect(() => {
    consoleLog("selectedPaymentMethod", selectedPaymentMethod);
  }, [selectedPaymentMethod]);

  // Set input address when changing isAddingNewCard or selectedPaymentMethod
  useEffect(() => {
    if (!isAddingNewCard) {
      if (selectedPaymentMethod) {
        const { billing_details } = selectedPaymentMethod;
        const { address, name, phone } = billing_details;
        const { city, country, line1, line2, postal_code, state } = address;
        setInputAddress({
          city,
          country,
          line1,
          line2,
          postal_code,
          state,
          firstname: name?.split(" ").length > 1 ? name?.split(" ")[0] : "",
          lastname:
            name?.split(" ").length > 1
              ? name?.split(" ")[1]
              : name?.split(" ")[0],
          phone,
        });
      }
    }
  }, [isAddingNewCard, selectedPaymentMethod]);

  // Set address expanded and input address when changing sameAsShippingAddress
  useEffect(() => {
    if (sameAsShippingAddress) {
      setAddressExpanded(false);
      setInputAddress({
        city: shippingAddress.city,
        country: shippingAddress.country,
        line1: shippingAddress.line1,
        line2: shippingAddress.line2,
        company: shippingAddress.company,
        postal_code: shippingAddress.postal_code,
        state: shippingAddress.state,
        firstname: shippingAddress.firstname,
        lastname: shippingAddress.lastname,
        phone: shippingAddress.phone,
      });
    } else {
      setAddressExpanded(true);
    }
  }, [sameAsShippingAddress]);

  // Set billing address when changing address fields
  useEffect(() => {
    const {
      city,
      country,
      line1,
      line2,
      postal_code,
      state,
      firstname,
      lastname,
      phone,
    } = inputAddress;
    setBillingAddress({
      address: {
        city,
        country,
        line1,
        line2,
        postal_code,
        state,
      },
      email: contact.email,
      name: firstname ? `${firstname} ${lastname}` : `${lastname}`,
      phone: phone ? phone : null,
    });
  }, [inputAddress]);

  const setPaymentIsBeingProcessed = (value: boolean) => {
    dispatch({
      type: checkoutActions.setPaymentIsBeingProcessed.type,
      payload: value,
    });
  };

  const submitBillingAddress = (value: any) => {
    dispatch({
      type: checkoutActions.submitBillingAddress.type,
      payload: value,
    });
  };

  // Handle Change
  const handleChange = (
    e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;

    switch (name) {
      case "paymentMethod":
        if (value === "addNewCard") {
          setIsAddingNewCard(true);
        } else {
          setIsAddingNewCard(false);
        }
        setSelectedPaymentMethod(
          savedPaymentMethods?.find((pm) => pm.id === value),
        );
        break;
      case "sameAsShippingAddress":
        if (value === "true") {
          setSameAsShippingAddress(true);
        } else {
          setSameAsShippingAddress(false);
        }
        break;
      default:
        setInputAddress({ ...inputAddress, [name]: value });
        break;
    }
  };

  // Handle Submit
  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    const cardElement = elements.getElement(CardNumberElement);

    try {
      setPaymentIsBeingProcessed(true);

      let paymentMethodId;

      if (isAddingNewCard && cardElement) {
        paymentMethodId = await stripe
          .createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: sameAsShippingAddress
              ? {
                  name: shippingAddress.firstname
                    ? `${shippingAddress.firstname} ${shippingAddress.lastname}`
                    : `${shippingAddress.lastname}`,
                  phone: shippingAddress.phone,
                  email: contact.email,
                  address: {
                    city: shippingAddress.city,
                    country: shippingAddress.country,
                    line1: shippingAddress.line1,
                    line2: shippingAddress.line2,
                    postal_code: shippingAddress.postal_code,
                    state: shippingAddress.state,
                  },
                }
              : {
                  name: inputAddress.firstname
                    ? `${inputAddress.firstname} ${inputAddress.lastname}`
                    : `${inputAddress.lastname}`,
                  phone: inputAddress.phone,
                  email: contact.email,
                  address: {
                    city: inputAddress.city,
                    country: inputAddress.country,
                    line1: inputAddress.line1,
                    line2: inputAddress.line2,
                    postal_code: inputAddress.postal_code,
                    state: inputAddress.state,
                  },
                },
          })
          .then((result) => {
            if (result.error) {
              throw new Error(result.error.message);
            }

            return result.paymentMethod.id;
          });

        // let attachedPaymentMethod = await
      } else {
        paymentMethodId = selectedPaymentMethod.id;
      }

      // if (!isAddingNewCard) {
      // }
      let updatedPaymentMethod = await updatePaymentMethod(
        sameAsShippingAddress
          ? {
              paymentMethodId,
              billingAddress: {
                city: shippingAddress.city,
                country: shippingAddress.country,
                line1: shippingAddress.line1,
                line2: shippingAddress.line2,
                postal_code: shippingAddress.postal_code,
                state: shippingAddress.state,
              },
              billingEmail: contact.email,
              billingName: shippingAddress.firstname
                ? `${shippingAddress.firstname} ${shippingAddress.lastname}`
                : `${shippingAddress.lastname}`,
              billingPhone: shippingAddress.phone,
            }
          : {
              paymentMethodId,
              billingAddress: {
                city: inputAddress.city,
                country: inputAddress.country,
                line1: inputAddress.line1,
                line2: inputAddress.line2,
                postal_code: inputAddress.postal_code,
                state: inputAddress.state,
              },
              billingEmail: contact.email,
              billingName: inputAddress.firstname
                ? `${inputAddress.firstname} ${inputAddress.lastname}`
                : `${inputAddress.lastname}`,
              billingPhone: inputAddress.phone,
            },
      );

      consoleLog("updatedPaymentMethod", updatedPaymentMethod);

      const { billing_details } = updatedPaymentMethod;

      submitBillingAddress({
        firstname:
          billing_details.name.split(" ").length === 2
            ? billing_details.name.split(" ")[0]
            : "",
        lastname:
          billing_details.name.split(" ").length === 2
            ? billing_details.name.split(" ")[1]
            : billing_details.name.split(" ")[0],
        line1: billing_details.address.line1,
        line2: billing_details.address.line2,
        country: billing_details.address.country,
        state: billing_details.address.state,
        city: billing_details.address.city,
        postal_code: billing_details.address.postal_code,
        phone: billing_details.phone,
      });

      let confirmCheckoutResult = await confirmCheckout({
        userId: "aidensung",
        paymentMethodId,
        items: cartItems,
        shipping: {
          address: {
            city: shippingAddress.city,
            country: shippingAddress.country,
            line1: shippingAddress.line1,
            line2: shippingAddress.line2,
            postal_code: shippingAddress.postal_code,
            state: shippingAddress.state,
          },
          name: shippingAddress.firstname
            ? `${shippingAddress.firstname} ${shippingAddress.lastname}`
            : `${shippingAddress.lastname}`,
          phone: shippingAddress.phone,
        },
      }).then((res) => {
        consoleLog("confirmCheckout response", res);
        return res;
      });

      if (!confirmCheckoutResult.invoiceId) {
        throw new Error("Something went wrong");
      } else {
        try {
          await emptyCart("aidensung");
        } catch (err: any) {
          throw new Error(err);
        }

        setPaymentIsBeingProcessed(false);
        history.push("/checkout/confirmation");
      }
    } catch (err: any) {
      setPaymentIsBeingProcessed(false);
      consoleLog(err);
      alert(err.message ? err?.message : "Something went wrong");
    }
  };

  return (
    <div className={classes.container}>
      <Link to="/" className={classes.logo}>
        LOGO
      </Link>
      <div className={classes.subContainer}>
        <form
          onSubmit={handleSubmit}
          autoComplete="chrome-off"
          className={classes.formContainer}
        >
          <CheckoutLinks checkoutRoutes={checkoutRoutes} />
          <div className={classes.previousProcessContainer}>
            <div className={classes.previousProcess}>
              <span>Contact</span>
              <span>{contact.email}</span>
              <Link to="information">Change</Link>
            </div>
            <div className={classes.previousProcess}>
              <span>Ship to</span>
              <span>
                {shippingAddress.line1 && `${shippingAddress.line1}, `}
                {shippingAddress.line2 && `${shippingAddress.line2}, `}
                {shippingAddress.city && `${shippingAddress.city}, `}
                {shippingAddress.state && `${shippingAddress.state}, `}
                {shippingAddress.postal_code &&
                  `${shippingAddress.postal_code}, `}
                {shippingAddress.country && `${shippingAddress.country}`}
              </span>
              <Link to="information">Change</Link>
            </div>
            <div className={classes.previousProcess}>
              <span>Method</span>
              <span>
                {_.startCase(checkoutShipping.method)}{" "}
                {checkoutShipping.price === 0
                  ? "FREE"
                  : `CAD $${checkoutShipping.price}`}
              </span>
              <Link to="shipping">Change</Link>
            </div>
          </div>
          <div className={classes.fieldsContainer}>
            <Accordion
              square
              expanded={expanded === "panel1"}
              onChange={handleAccordionChange("panel1")}
            >
              <AccordionSummary
                aria-controls="panel1d-content"
                id="panel1d-header"
              >
                <label>
                  <span>
                    <span></span>
                  </span>
                  <span>Credit card</span>
                  <span></span>
                </label>
              </AccordionSummary>
              <AccordionDetails>
                {savedPaymentMethods ? (
                  <div className={classes.inputsContainer}>
                    <div
                      className={`${classes.inputContainer} ${classes.selectInputContainer}`}
                    >
                      <select
                        onChange={handleChange}
                        name="paymentMethod"
                        value={selectedPaymentMethod?.id}
                      >
                        {savedPaymentMethods.map((pm) => (
                          <option key={pm.id} value={pm.id}>
                            {pm.card.brand.toUpperCase()} - Ending with{" "}
                            {pm.card.last4}
                          </option>
                        ))}
                        <option value="addNewCard">ADD A NEW CARD</option>
                      </select>
                      <label>Saved card</label>
                    </div>
                    {isAddingNewCard ? (
                      <div className={classes.inputsContainer}>
                        <label className={classes.stripeElementContainer}>
                          <span>Card number</span>
                          <CardNumberElement
                            options={{
                              style: stripeElementStyle,
                              placeholder: "",
                            }}
                          />
                        </label>
                        <label className={classes.stripeElementContainer}>
                          <span>Expiration date</span>
                          <CardExpiryElement
                            options={{
                              style: stripeElementStyle,
                              placeholder: "",
                            }}
                          />
                        </label>
                        <label className={classes.stripeElementContainer}>
                          <span>CVC</span>
                          <CardCvcElement
                            options={{
                              style: stripeElementStyle,
                              placeholder: "",
                            }}
                          />
                        </label>
                      </div>
                    ) : (
                      <div className={classes.inputsContainer}>
                        <div
                          className={`${classes.inputContainer} ${classes.textInputContainer}`}
                        >
                          <input
                            value={`**** **** **** ${selectedPaymentMethod?.card.last4}`}
                            readOnly
                          />
                          <label>Card number</label>
                        </div>
                        <div
                          className={`${classes.inputContainer} ${classes.textInputContainer}`}
                        >
                          <input
                            value={`${selectedPaymentMethod?.billing_details.name}`}
                            readOnly
                          />
                          <label>Name on Card</label>
                        </div>
                        <div
                          className={`${classes.inputContainer} ${classes.textInputContainer}`}
                        >
                          <input
                            value={`${selectedPaymentMethod?.card.exp_month}/${selectedPaymentMethod?.card.exp_year}`}
                            readOnly
                          />
                          <label>Expiration date</label>
                        </div>
                        <div className={classes.twoColumnInputContainer}>
                          <div
                            className={`${classes.inputContainer} ${classes.textInputContainer}`}
                          >
                            <input
                              value={`${
                                selectedPaymentMethod?.billing_details.address
                                  .postal_code ?? "-"
                              }`}
                              readOnly
                            />
                            <label>Postal Code</label>
                          </div>
                          <div
                            className={`${classes.inputContainer} ${classes.textInputContainer}`}
                          >
                            <input
                              value={`${
                                selectedPaymentMethod?.card.country ?? "-"
                              }`}
                              readOnly
                            />
                            <label>Country</label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Loading on size="1.5rem" />
                )}
              </AccordionDetails>
            </Accordion>
            <Accordion
              square
              expanded={expanded === "panel2"}
              onChange={handleAccordionChange("panel2")}
            >
              <AccordionSummary
                aria-controls="panel2d-content"
                id="panel2d-header"
              >
                <label>
                  <span>
                    <span></span>
                  </span>
                  <span>Paypal</span>
                  <span></span>
                </label>
              </AccordionSummary>
              <AccordionDetails>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                Suspendisse malesuada lacus ex, sit amet blandit leo lobortis
                eget. Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                Suspendisse malesuada lacus ex, sit amet blandit leo lobortis
                eget.
              </AccordionDetails>
            </Accordion>
            <Accordion
              square
              expanded={expanded === "panel3"}
              onChange={handleAccordionChange("panel3")}
            >
              <AccordionSummary
                aria-controls="panel3d-content"
                id="panel3d-header"
              >
                <label>
                  <span>
                    <span></span>
                  </span>
                  <span>Alipay</span>
                  <span></span>
                </label>
              </AccordionSummary>
              <AccordionDetails>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                Suspendisse malesuada lacus ex, sit amet blandit leo lobortis
                eget. Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                Suspendisse malesuada lacus ex, sit amet blandit leo lobortis
                eget.
              </AccordionDetails>
            </Accordion>
          </div>
          <div className={classes.fieldsContainer}>
            <div className={classes.fieldsHeader}>
              <div className={classes.fieldsHeaderTitle}>Billing address</div>
            </div>
            <div className={classes.fieldsBody}>
              <div
                role="group"
                aria-labelledby="billing-address-radio-group"
                className={classes.radioGroup}
              >
                <label className={classes.radioLabel}>
                  <input
                    onChange={handleChange}
                    type="radio"
                    name="sameAsShippingAddress"
                    checked={sameAsShippingAddress}
                    value="true"
                  />
                  <span>
                    <span></span>
                  </span>
                  <span>Same as shipping address</span>
                  <span></span>
                </label>
                <Accordion
                  square
                  expanded={AddressExpanded}
                  onClick={() => setSameAsShippingAddress(false)}
                >
                  <AccordionSummary
                    aria-controls="panel4d-content"
                    id="panel4d-header"
                    style={{
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    <label
                      className={`${classes.radioLabel} ${classes.labelInAccordion}`}
                    >
                      <input
                        onChange={handleChange}
                        type="radio"
                        name="sameAsShippingAddress"
                        checked={!sameAsShippingAddress}
                        value="false"
                      />
                      <span>
                        <span></span>
                      </span>
                      <span>Use a different billing address</span>
                      <span></span>
                    </label>
                  </AccordionSummary>
                  <AccordionDetails>
                    {!sameAsShippingAddress ? (
                      <div className={classes.inputsContainer}>
                        <div className={classes.twoColumnInputContainer}>
                          <div
                            className={`${classes.inputContainer} ${classes.textInputContainer}`}
                          >
                            <input
                              type="text"
                              name="firstname"
                              onChange={handleChange}
                              disabled={sameAsShippingAddress}
                            />
                            <label htmlFor="firstname">
                              First name (optional)
                            </label>
                            <span />
                          </div>
                          <div
                            className={`${classes.inputContainer} ${classes.textInputContainer}`}
                          >
                            <input
                              type="text"
                              name="lastname"
                              onChange={handleChange}
                              disabled={sameAsShippingAddress}
                              required
                            />
                            <label htmlFor="lastname">Last name</label>
                            <span />
                          </div>
                        </div>
                        <div
                          className={`${classes.inputContainer} ${classes.textInputContainer}`}
                        >
                          <input
                            type="text"
                            name="company"
                            onChange={handleChange}
                            disabled={sameAsShippingAddress}
                          />
                          <label htmlFor="company">Company (optional)</label>
                          <span />
                        </div>
                        <div
                          className={`${classes.inputContainer} ${classes.textInputContainer}`}
                        >
                          <input
                            type="text"
                            name="line1"
                            onChange={handleChange}
                            disabled={sameAsShippingAddress}
                            required
                          />
                          <label htmlFor="line1">Address</label>
                          <span />
                        </div>
                        <div
                          className={`${classes.inputContainer} ${classes.textInputContainer}`}
                        >
                          <input
                            type="text"
                            name="line2"
                            onChange={handleChange}
                            disabled={sameAsShippingAddress}
                          />
                          <label htmlFor="line2">
                            Apartment, suite, etc. (optional)
                          </label>
                          <span />
                        </div>
                        <div className={classes.twoColumnInputContainer}>
                          <div
                            className={`${classes.inputContainer} ${classes.selectInputContainer}`}
                          >
                            <select
                              name="country"
                              onChange={handleChange}
                              defaultValue={inputAddress.country}
                              disabled={sameAsShippingAddress}
                              required
                            >
                              <option value="" disabled hidden></option>
                              <option value="CA">Canada</option>
                              <option value="US">USA</option>
                              <option value="China">China</option>
                              <option value="SouthKorea">South Korea</option>
                            </select>
                            <label htmlFor="country">Country</label>
                            <span />
                          </div>
                          <div
                            className={`${classes.inputContainer} ${classes.selectInputContainer}`}
                          >
                            <select
                              name="state"
                              onChange={handleChange}
                              defaultValue={inputAddress.state}
                              disabled={sameAsShippingAddress}
                              required
                            >
                              <option value="" disabled hidden></option>
                              <option value="ON">Ontario</option>
                              <option value="BC">British Columbia</option>
                            </select>
                            <label htmlFor="state">Province</label>
                            <span />
                          </div>
                        </div>
                        <div className={classes.twoColumnInputContainer}>
                          <div
                            className={`${classes.inputContainer} ${classes.textInputContainer}`}
                          >
                            <input
                              type="text"
                              name="city"
                              onChange={handleChange}
                              disabled={sameAsShippingAddress}
                              required
                            />
                            <label htmlFor="city">City</label>
                            <span />
                          </div>
                          <div
                            className={`${classes.inputContainer} ${classes.textInputContainer}`}
                          >
                            <input
                              type="text"
                              name="postal_code"
                              onChange={handleChange}
                              disabled={sameAsShippingAddress}
                              required
                            />
                            <label htmlFor="postal_code">Postal Code</label>
                            <span />
                          </div>
                        </div>
                        <div
                          className={`${classes.inputContainer} ${classes.textInputContainer}`}
                        >
                          <input
                            type="text"
                            name="phone"
                            onChange={handleChange}
                            disabled={sameAsShippingAddress}
                          />
                          <label htmlFor="phone">Phone (Recommended)</label>
                          <span />
                        </div>
                      </div>
                    ) : null}
                  </AccordionDetails>
                </Accordion>
              </div>
            </div>
          </div>
          <div className={classes.fieldsContainer}>
            <div className={classes.fieldsHeader}>
              <div className={classes.fieldsHeaderTitle}>Remember</div>
            </div>
            <div className={classes.fieldsBody}>
              <label className={classes.checkboxLabel}>
                <input type="checkbox" name="remember" />
                <span>
                  <span></span>
                </span>
                Save card for a faster payment
              </label>
            </div>
          </div>
          <div className={classes.btnContainer}>
            <StripeIcon height="0" />
            <button type="submit" disabled={!stripe} className={classes.payBtn}>
              Pay now
            </button>
          </div>
        </form>
      </div>
      <CheckoutSummary />
      <OrderIsBeingProcessed isBeingProcessed={isBeingProcessed} />
      <Loading fullScreen on={isLoading} size="3rem" />
    </div>
  );
};

export default withAuth(CheckoutPaymentPage);
